import idMixin from '@/mixins/aha-id';
import dropdownMixin from '@/mixins/aha-dropdown';
import { selectAll, getAttr } from 'bootstrap-vue/src/utils/dom';
import KeyCodes from 'bootstrap-vue/src/utils/key-codes';
import inputEventBus from '@/mixins/input-eventbus';

export default {
    mixins: [idMixin, dropdownMixin],
    render(h) {
        const toggle = h(
            'button', {
                ref: 'toggle',
                attrs: {
                    type: 'button',
                    id: this.safeId('_AHA_toggle_'),
                    'aria-haspopup': this.role,
                    'aria-expanded': this.visible ? 'true' : 'false'
                },
                class: this.toggleClasses,
                on: {
                    click: this.toggle,
                    keydown: this.toggle
                }
            }, [this.buttonText]
        );

        const arrow = h(
            'span', {
                ref: 'arrow',
                attrs: {
                    'x-arrow': ''
                },
                class: 'c-nav-dropdown__arrow'
            }
        );

        const menu = h(
            this.dropdownTag, {
                ref: 'menu',
                class: this.menuClasses,
                attrs: {
                    tabindex: -1,
                    role: this.role,
                    'aria-activedescendant': this.activeItemId,
                    'aria-labelledby': this.safeId('_AHA_toggle_')
                },
                on: {
                    keydown: this.onListboxKeydown
                }
            }, [this.$slots.dropdown, this.arrow ? arrow : null]
        );

        const hiddenInput = h(
            'input', {
                ref: 'input',
                attrs: {
                    type: 'hidden',
                    value: this.hiddenValue,
                    name: this.inputName
                },
                directives: [
                    this.directiveOptions
                ]
            }
        );

        return h(
            'div', {
                ref: 'dropdown',
                attrs: {
                    id: this.safeId()
                },
                class: this.dropdownClasses
            }, [toggle, hiddenInput, menu]
        );
    },
    data() {
        return {
            hiddenValue: null,
            displayText: null,
            activeItemId: null,
            dropdownItems: [],
            directiveOptions: {}
        };
    },
    props: {
        value: {
            required: false
        },
        defaultValue: {
            type: String,
            required: true
        },
        labelledby: {
            type: String,
            default: ''
        },
        toggleClass: {
            type: String,
            default: ''
        },
        menuClass: {
            type: String,
            default: ''
        },
        role: {
            type: String,
            default: 'menu'
        },
        arrow: {
            type: Boolean,
            default: false
        },
        inputName: {
            type: String,
            default: 'searchCategory'
        },
        dropdownTag: {
            type: String,
            default: 'div'
        },
        dropdownItemActiveClass: {
            type: String,
            default: 'selected'
        },
        inputDirective: {
            type: Boolean,
            default: false
        }
    },
    mounted() {
        this.$on('selected', this.selectedListener);
        this.$on('register', this.registerDropdownItem);

        if(this.inputDirective) {
            this.directiveOptions = {
                name: 'input',
                value: this.inputName
            };
        }

        this.$nextTick(() => {
            if(this.defaultValue) {
                this.hiddenValue = this.defaultValue;

                // If we have a default value, set the item to be selected on first menu opening
                const item = this.dropdownItems.find(item => item.value === this.defaultValue || item.text === this.defaultValue);

                if(typeof item !== 'undefined') {
                    item.selected = true;
                    // This next line actually sets the item as selected
                    this.selectedListener(item.value, item.text, item.element);
                }
            }
        });
    },
    methods: {
        onListboxKeydown(evt) {
            this.onKeydown(evt);

            // Select the currently selected dropdown item
            if(!this.disableMenuEvents && (evt.keyCode === KeyCodes.ENTER || evt.keyCode === KeyCodes.SPACE)) {
                let item = this.currentSelectedDropdown();
                this.selectedListener(item.value, item.text, item.element);
            }
        },
        selectedListener(value, text, element, isInitial) {
            if(typeof value !== 'undefined') {
                this.hiddenValue = value;
                this.displayText = text;

                this.setSelectedDropdownItem(value);

                this.$emit('input', this.hiddenValue);
                inputEventBus.$emit('inputUpdated', this.hiddenValue, this.inputName, this.$el);
                this.focusItemByElement(element);

                // Disable focus on initial page load
                if(!isInitial) {
                    this.focusToggler();
                }
            }
        },
        getItems() {
            return this.filterVisible(selectAll('[role]', this.$refs.menu));
        },
        registerDropdownItem(value, text, element) {
            this.dropdownItems.push({
                value,
                text,
                element,
                selected: false
            });
        },
        showMenuFocus() {
            this.$nextTick(() => {
                this.$nextTick(() => {
                    const item = this.dropdownItems.find(item => item.selected);

                    if(typeof item !== 'undefined') {
                        this.setSelectedDropdownItem(item.value);
                        this.focusItemByElement(item.element);
                    }

                    this.$refs.menu.focus();
                });
            });
        },
        setSelectedDropdownItem(value) {
            this.dropdownItems.forEach(item => {
                item.selected = false;

                if(item.value === value) {
                    item.selected = true;
                }
            });
        },
        focusItem(index, items) {
            let element = items.find((element, i) => i === index);

            this.setSelectedDropdownClasses(element, items);
        },
        focusItemByElement(element) {
            const items = this.getItems();

            let index = items.indexOf(items.find(item => item === element));

            this.focusItem(index, items);
        },
        focusNext(evt, up) {
            if(!this.visible) {
                return;
            }

            evt.preventDefault();
            evt.stopPropagation();

            this.$nextTick(() => {
                const items = this.getItems();

                if(items.length < 1) {
                    return;
                }

                let index = items.indexOf(items.find(el => el.classList.contains(this.dropdownItemActiveClass)));

                if(up && index > 0) {
                    index--;
                } else if(!up && index < items.length - 1) {
                    index++;
                }

                if(index < 0) {
                    index = 0;
                }

                this.focusItem(index, items);
            });
        },
        setSelectedDropdownClasses(selectedElement, elements) {
            elements.forEach(el => {
                el.classList.remove(this.dropdownItemActiveClass);
                if(selectedElement.tagName !== 'A') {
                    el.setAttribute('aria-selected', false);
                }
            });

            if(selectedElement && getAttr(selectedElement, 'tabindex') !== '-1') {
                selectedElement.classList.add(this.dropdownItemActiveClass);
                if(selectedElement.tagName !== 'A') {
                    selectedElement.setAttribute('aria-selected', true);
                }
                this.activeItemId = selectedElement.getAttribute('id');
            }
        },
        currentSelectedDropdown() {
            return this.dropdownItems.find(item => item.element.classList.contains(this.dropdownItemActiveClass));
        },
        resetList() {
            const item = this.dropdownItems[0];
            this.selectedListener(item.value, item.text, item.element);
        }
    },
    watch: {
        value(val) {
            const dropdown = this.dropdownItems.find(item => item.value === val);

            // If the value is trying to be set programmatically, check the registered dropdown items
            if(typeof dropdown !== 'undefined') {
                this.hiddenValue = val;
                this.displayText = dropdown.text;

                this.setSelectedDropdownItem(dropdown.value);
            }
        }
    },
    computed: {
        buttonText() {
            return this.displayText || this.getDefaultValue;
        },
        getDefaultValue() {
            const item = this.dropdownItems.find(item => item.value === this.defaultValue);

            if(typeof item !== 'undefined') {
                return item.text;
            } else {
                return this.defaultValue;
            }
        },
        dropdownClasses() {
            return [
                'c-select-dropdown__container',
                'position-static',
                this.visible ? 'show' : ''
            ];
        },
        menuClasses() {
            return [
                'c-select-dropdown',
                'dropdown-menu',
                this.right ? 'dropdown-menu-right' : '',
                this.menuClass
            ];
        },
        toggleClasses() {
            return [this.toggleClass];
        }
    }
};



// WEBPACK FOOTER //
// ./src/app/components/aha-listbox.js